import { useState, useEffect } from 'react';
import { X, Share, PlusSquare } from 'lucide-react';

const DISMISS_KEY = 'ios_install_dismissed_until';

function isIOS() {
  const ua = navigator.userAgent;
  return /iPhone|iPad/.test(ua) && !window.MSStream;
}

function isStandalone() {
  return window.navigator.standalone === true
    || window.matchMedia('(display-mode: standalone)').matches;
}

function isDismissed() {
  try {
    const until = localStorage.getItem(DISMISS_KEY);
    if (!until) return false;
    return Date.now() < parseInt(until, 10);
  } catch { return false; }
}

export default function IOSInstallBanner() {
  const [visible, setVisible] = useState(false);
  const [animIn, setAnimIn] = useState(false);

  useEffect(() => {
    if (!isIOS() || isStandalone() || isDismissed()) return;
    setVisible(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setAnimIn(true)));
  }, []);

  if (!visible) return null;

  const dismiss = (days) => {
    if (days > 0) {
      localStorage.setItem(DISMISS_KEY, String(Date.now() + days * 86400000));
    }
    setAnimIn(false);
    setTimeout(() => setVisible(false), 300);
  };

  return (
    <div
      data-testid="ios-install-banner"
      className={`mx-4 mt-3 rounded-xl bg-zinc-900 border border-[#E84C1C]/30 overflow-hidden transition-all duration-300 ${animIn ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
    >
      <div className="h-0.5 bg-gradient-to-r from-[#E84C1C] via-orange-400 to-[#E84C1C]" />
      <div className="p-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#E84C1C]/10 flex items-center justify-center shrink-0 mt-0.5">
            <Share size={18} className="text-[#E84C1C]" />
          </div>
          <div className="flex-1 min-w-0">
            <p data-testid="ios-install-title" className="text-sm font-semibold text-zinc-100">
              Instalá Sushi Express en tu iPhone
            </p>
            <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
              Tocá el botón compartir y luego "Agregar a pantalla de inicio"
            </p>

            {/* Visual guide */}
            <div className="flex items-center gap-3 mt-3 p-2.5 rounded-lg bg-zinc-800/60 border border-zinc-700/40">
              <div className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-lg bg-blue-500/15 flex items-center justify-center">
                  <Share size={16} className="text-blue-400" />
                </div>
                <span className="text-[10px] text-zinc-500">Compartir</span>
              </div>
              <div className="text-zinc-600 text-xs">luego</div>
              <div className="flex flex-col items-center gap-1">
                <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                  <PlusSquare size={16} className="text-emerald-400" />
                </div>
                <span className="text-[10px] text-zinc-500">Agregar</span>
              </div>
            </div>
          </div>
          <button
            data-testid="ios-install-close-btn"
            onClick={() => dismiss(7)}
            className="text-zinc-600 hover:text-zinc-400 transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex gap-2 mt-3">
          <button
            data-testid="ios-install-ok-btn"
            onClick={() => dismiss(0)}
            className="flex-1 py-2 text-xs font-medium bg-[#E84C1C] hover:bg-[#d4411a] text-white rounded-lg transition-colors"
          >
            Entendido
          </button>
          <button
            data-testid="ios-install-dismiss-btn"
            onClick={() => dismiss(7)}
            className="flex-1 py-2 text-xs font-medium text-zinc-400 border border-zinc-700 rounded-lg hover:bg-zinc-800/50 transition-colors"
          >
            Ahora no
          </button>
        </div>
      </div>
    </div>
  );
}
